/**
 * MODAL: CRÉATION PRODUIT
 * Formulaire de création d'un nouveau produit avec validation Zod
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { CreateProductInput } from '../../schemas/product';
import { createProductSchema } from '../../schemas/product';
import { Form, FormInput } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProductInput) => Promise<void>;
}

export function CreateProductModal({ isOpen, onClose, onSubmit }: CreateProductModalProps) {
  const form = useForm<CreateProductInput>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      name: '',
    },
  });

  const handleFormSubmit = async (data: CreateProductInput) => {
    try {
      await onSubmit(data);
      form.reset(); // Réinitialiser le formulaire après succès
      onClose();
    } catch (error) {
      console.error('Erreur lors de la création du produit :', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nouveau produit"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer le produit"
          isSubmitting={form.formState.isSubmitting}
          formId="create-product-form"
        />
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} id="create-product-form">
          <div className="space-y-4">
            <FormInput
              control={form.control}
              name="name"
              label="Nom du produit"
              placeholder="Ex: 4L Trophy 2025"
              required
            />

            <FormInput
              control={form.control}
              name="start_date"
              label="Date de début"
              type="date"
              required
            />

            <FormInput
              control={form.control}
              name="end_date"
              label="Date de fin"
              type="date"
              required
            />
          </div>
        </form>
      </Form>
    </Modal>
  );
}
